/**
 * Sheet slide-over panel.
 *
 * Opens a right-side panel on desktop, bottom sheet on mobile.
 * CSS: SeeMe's sheets.less (imported via rf-compat.less)
 *
 * Trigger:
 *   <a href="#" data-sheet="{% url 'my-view' obj.pk %}">Open</a>
 *
 * Content partials use marker elements for slotting:
 *   [data-sheet-toolbar]   → toolbar left side
 *   [data-sheet-actions]   → toolbar right side (before close button)
 *   [data-sheet-banner]    → state banner, between the toolbar and the header
 *   [data-sheet-actionbar] → second chrome row, below the header
 *   [data-sheet-fixed]     → controls pinned between the action bar and the scrolling body
 *   [data-sheet-footer]    → footer area
 *   [data-sheet-compact]  → compact body padding flag
 *   [data-sheet-full]     → full-screen mobile mode flag
 *   [data-sheet-scroll-to] → scrolled into view when the sheet loads
 *   [data-sheet-scroll-to="top"] → its top goes to the top of the sheet body
 *
 * Dismiss:
 *   <button data-dismiss="sheet">Cancel</button>
 *   Or: Escape key, backdrop click, close button
 *
 * URL history:
 *   ?sheet=/url/ is written to the URL so sheets survive refresh
 *   and work with back/forward navigation.
 *
 * Back arrow:
 *   A sheet opened from a link inside another sheet gets a `.sheet__btn`
 *   arrow at the left end of the header, before the title, that returns to
 *   the sheet it came from (history.back(), so the browser's Back button is
 *   the same move). The header, not the toolbar: the toolbar's label + status
 *   + three actions + close already fill a 390px phone.
 *   The shell tracks the trail itself; partials render no breadcrumb. The
 *   arrow shows even when the header's subject line links to that sheet; it
 *   is left out only for a legacy `.breadcrumb` in the body that does.
 *   Back reloads that sheet and puts its body back where it was scrolled,
 *   once the requests its body starts (lazy hx-trigger="load" cards) are in.
 *
 * Return to the sheet this one was opened from:
 *   <button data-sheet-return="/fallback/url/">Cancel</button>
 *   Goes Back (the trail's sheet reloads, fresh, at its scroll position) when
 *   there is a trail; with none (a pasted ?sheet= URL), opens the fallback in
 *   this sheet's place.
 *   A form sheet's Cancel and, through the server's `sheetBack` trigger
 *   (htmx-actions.js), its save use it, so finishing an edit lands on the
 *   sheet's existing history entry instead of stacking a second one.
 *
 * Programmatic:
 *   window.rfSheet.open(url)
 *   window.rfSheet.open(url, { history: 'replace' })
 *   window.rfSheet.back(fallbackUrl)
 *   window.rfSheet.setUrl(url)
 *   window.rfSheet.close()
 *   window.rfSheet.refresh()
 */

(function () {
    'use strict';

    var backdrop = null;
    var activeSheet = null;

    // Sheets the user came through to reach the active one, oldest first:
    // the URL each was loaded from, the title it showed, and how far its body
    // was scrolled (Back restores it). Non-empty means the header gets a back
    // arrow. Cleared when a sheet opens from the page or the sheet closes;
    // popped when Back lands on the entry's URL.
    var sheetTrail = [];
    var activeUrl = null;
    var sheetPagePath = null;   // pathname of the page under the open sheet

    // A Back reload's scroll restore, waiting on the sheet body's own htmx
    // requests: { body, top, pending }. Null when no restore is waiting.
    var scrollRestore = null;

    // Skeleton placeholder shown while sheet content loads
    var SKELETON =
        '<div style="padding: 20px 16px;">' +
            '<div class="rf-skeleton rf-skeleton--heading"></div>' +
            '<div class="rf-skeleton rf-skeleton--text"></div>' +
            '<div class="rf-skeleton rf-skeleton--text"></div>' +
            '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short"></div>' +
        '</div>';

    // ── Backdrop ──────────────────────────────────────────────────────────

    function getBackdrop() {
        if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.className = 'backdrop backdrop--faded';
            document.body.appendChild(backdrop);
        }
        return backdrop;
    }

    // ── Open / Close ─────────────────────────────────────────────────────

    function openSheet(el) {
        if (!el) return;
        var bd = getBackdrop();
        void bd.offsetHeight;
        bd.classList.add('show');
        void el.offsetHeight;
        el.classList.add('show');
        activeSheet = el;
        document.body.style.overflow = 'hidden';
    }

    function closeSheet(el) {
        el = el || activeSheet;
        if (!el) return;

        var params = new URLSearchParams(window.location.search);
        if (params.has('sheet')) {
            params.delete('sheet');
            // The hash stays: shell.js keeps the open rail section there (#rail-<section>).
            var newUrl = (params.toString()
                ? window.location.pathname + '?' + params.toString()
                : window.location.pathname) + window.location.hash;
            var state = history.state ? Object.assign({}, history.state) : {};
            history.replaceState(state, '', newUrl);
        }

        el.classList.remove('show');
        getBackdrop().classList.remove('show');
        activeSheet = null;
        activeUrl = null;
        sheetTrail = [];
        document.body.style.overflow = '';

        if (el.hasAttribute('data-sheet-dynamic')) {
            el.addEventListener('transitionend', function handler() {
                el.removeEventListener('transitionend', handler);
                if (el.parentNode) el.parentNode.removeChild(el);
            });
        }
    }

    // ── URL history ──────────────────────────────────────────────────────

    function setSheetParam(url, mode) {
        var params = new URLSearchParams(window.location.search);
        params.set('sheet', url);
        var newUrl = window.location.pathname + '?' + params.toString().replace(/%2F/gi, '/')
            + window.location.hash;   // the open rail section (#rail-<section>, shell.js)
        var fn = mode === 'push' ? history.pushState : history.replaceState;
        // Own the entry's state. Copying the current state forward carried
        // htmx's `htmx: true` marker onto every ?sheet= entry, so going back to
        // one went to htmx's own popstate handler, which restores from a
        // history cache that index.js disables — and a miss there is a full
        // reload. With a sheet-owned marker the entry falls through to
        // htmx-nav's handler, which leaves ?sheet= URLs to the code below.
        fn.call(history, { rfSheet: true }, '', newUrl);
        sheetPagePath = window.location.pathname;
    }

    // ── Back arrow ───────────────────────────────────────────────────────

    function currentTitle(el) {
        var header = el.querySelector('.sheet__header');
        if (!header) return '';
        var h3 = header.querySelector('h3');
        if (h3 && h3.textContent.trim()) return h3.textContent.trim();
        var field = header.querySelector('input, textarea');
        return field ? field.value.trim() : '';
    }

    // Record the active sheet as the one to return to, before it is replaced.
    function pushTrail() {
        if (!activeSheet || !activeUrl) return;
        sheetTrail.push({
            url: activeUrl,
            title: currentTitle(activeSheet),
            scrollTop: activeSheet.querySelector('.sheet__body').scrollTop
        });
    }

    function renderBackArrow(el) {
        var existing = el.querySelector('[data-sheet-back]');
        if (existing) existing.remove();
        var header = el.querySelector('.sheet__header');
        // The header element outlives each populate, so clear the arrow
        // layout before deciding whether this sheet gets one.
        if (header) header.classList.remove('justify-content-start', 'gap-2');
        // A sheet with no title hides its header; the toolbar slot is the fallback.
        var slot = header && header.style.display !== 'none'
            ? header
            : el.querySelector('.sheet__toolbar > div:first-child');
        if (!slot) return;
        var prev = sheetTrail[sheetTrail.length - 1];
        if (!prev) return;

        // An unmigrated sheet's legacy body breadcrumb already goes back to
        // that sheet; an arrow would be a second back control. The header's
        // subject line does not count: it names the record the sheet is about,
        // and the arrow is how every stacked sheet goes back.
        var prevPath = prev.url.split('?')[0];
        var links = el.querySelectorAll('.sheet__body .breadcrumb a[data-sheet]');
        var duplicate = Array.prototype.some.call(links, function (a) {
            return a.getAttribute('data-sheet').split('?')[0] === prevPath;
        });
        if (duplicate) return;

        var btn = document.createElement('button');
        btn.type = 'button';
        // The header centers its children; the arrow belongs on the title
        // line, not the middle of a three-line title block.
        btn.className = 'sheet__btn align-self-start';
        btn.setAttribute('data-sheet-back', '');
        btn.title = prev.title ? 'Back to ' + prev.title : 'Back';
        btn.setAttribute('aria-label', btn.title);
        btn.innerHTML = '<i class="fa-solid fa-arrow-left"></i>';
        slot.insertBefore(btn, slot.firstChild);
        // .sheet__header spreads its children for title + close; with the
        // arrow first, that would push the title to the far edge.
        if (slot === header) header.classList.add('justify-content-start', 'gap-2');
    }

    // ── Populate sheet with fetched HTML ─────────────────────────────────

    function populateSheet(el, html) {
        var body = el.querySelector('.sheet__body');
        body.style.visibility = 'hidden';
        body.innerHTML = html;

        var header = el.querySelector('.sheet__header');
        header.innerHTML = '<h3></h3>';
        header.style.display = '';

        // dropdowns.js reads data-sheet-title on a [data-dropdown] trigger as the menu's own sheet title, so skip those.
        var titleInput = body.querySelector(
            '[data-sheet-title]:not([data-dropdown]), ' +
            'input[name="title"], textarea[name="title"], ' +
            'input[name="name"], textarea[name="name"]'
        );
        if (titleInput) {
            // If the title input is inside a form, associate it via the HTML5
            // form attribute so it still submits with the form after being
            // lifted into the sheet header.
            var parentForm = titleInput.closest('form');
            if (parentForm) {
                if (!parentForm.id) parentForm.id = 'sheet-form-' + Date.now();
                titleInput.setAttribute('form', parentForm.id);
            }
            header.querySelector('h3').remove();
            header.appendChild(titleInput);
        } else {
            var bodyH3 = body.querySelector('h3');
            if (bodyH3) {
                header.querySelector('h3').textContent = bodyH3.textContent;
                bodyH3.remove();
            } else {
                header.style.display = 'none';
            }
        }

        var compactFlag = body.querySelector('[data-sheet-compact]');
        if (compactFlag) {
            body.classList.add('sheet__body--compact');
            compactFlag.remove();
        } else {
            body.classList.remove('sheet__body--compact');
        }

        var fullFlag = body.querySelector('[data-sheet-full]');
        if (fullFlag) {
            el.classList.add('sheet--full');
            fullFlag.remove();
        } else {
            el.classList.remove('sheet--full');
        }

        var wideFlag = body.querySelector('[data-sheet-wide]');
        if (wideFlag) {
            el.classList.add('sheet--wide');
            wideFlag.remove();
        } else {
            el.classList.remove('sheet--wide');
        }

        var toolbarLeft = el.querySelector('.sheet__toolbar > div:first-child');
        if (toolbarLeft) {
            var toolbarContent = body.querySelector('[data-sheet-toolbar]');
            if (toolbarContent) {
                // Move the wrapper itself (not just its children) into the pinned
                // slot, so its id survives and stays OOB-targetable. The wrapper
                // becomes `.sheet__toolbar > div:first-child`, so the existing flex
                // rule lays its children out exactly as before.
                toolbarLeft.replaceWith(toolbarContent);
            } else {
                // No toolbar marker — reset to a pristine empty slot. The shell is
                // built once and reused across opens, so a fresh div avoids leaking
                // a prior sheet's id/attrs into this one.
                toolbarLeft.replaceWith(document.createElement('div'));
            }
        }
        renderBackArrow(el);

        // State banner — a `.state-banner` the partial marks with
        // [data-sheet-banner] is lifted out of the body and placed between the
        // toolbar and the header, so it pins with the chrome and never fights
        // the body's sticky jump strip. The element itself moves (not its
        // children), so its id survives and an out-of-band swap after Snooze
        // / Unsnooze can replace it in place. A record with two states marks
        // two bars; they land in document order, the first one on top. Reset
        // on every populate, like the toolbar slot.
        el.querySelectorAll(':scope > [data-sheet-banner]').forEach(function (b) { b.remove(); });
        body.querySelectorAll('[data-sheet-banner]').forEach(function (b) { header.before(b); });

        var actionsContainer = el.querySelector('.overlay-toolbar__actions');
        if (actionsContainer) {
            var closeBtn = actionsContainer.querySelector('.sheet__close');
            actionsContainer.innerHTML = '';
            if (closeBtn) actionsContainer.appendChild(closeBtn);

            var actionsContent = body.querySelector('[data-sheet-actions]');
            if (actionsContent) {
                while (actionsContent.firstChild) {
                    actionsContainer.insertBefore(actionsContent.firstChild, closeBtn);
                }
                actionsContent.remove();
            }
        }

        // Second chrome row under the header, for record-level actions that no
        // longer fit beside a wide toolbar control. Optional — a partial that
        // marks none gets a hidden bar. The shell is reused across opens, so
        // this resets on every populate the same way the footer does.
        var actionbar = el.querySelector('.sheet__actionbar');
        if (actionbar) {
            actionbar.innerHTML = '';
            var actionbarContent = body.querySelector('[data-sheet-actionbar]');
            if (actionbarContent) {
                while (actionbarContent.firstChild) {
                    actionbar.appendChild(actionbarContent.firstChild);
                }
                actionbarContent.remove();
                actionbar.style.display = '';
            } else {
                actionbar.style.display = 'none';
            }
        }

        // Controls that stay visible while the body scrolls under them (the lead conversation's
        // chips, recipient and line).  The element itself moves, like a banner, so its id survives
        // for an out-of-band swap.  Reset on every populate.
        el.querySelectorAll(':scope > [data-sheet-fixed]').forEach(function (f) { f.remove(); });
        body.querySelectorAll('[data-sheet-fixed]').forEach(function (f) { body.before(f); });

        var footer = el.querySelector('.sheet__footer');
        if (footer) {
            footer.removeAttribute('hidden');
            footer.innerHTML = '';
            var footerContent = body.querySelector('[data-sheet-footer]');
            if (footerContent) {
                while (footerContent.firstChild) {
                    footer.appendChild(footerContent.firstChild);
                }
                footerContent.remove();
                footer.style.display = '';
            } else {
                footer.style.display = 'none';
            }
        }

        // Scripts inserted via innerHTML don't execute; recreate them to force eval.
        // Skip non-JS data blocks (e.g. {% json_script %} type="application/json") —
        // they don't need execution and re-creation would strip id/type, breaking
        // document.getElementById lookups against the data payload.
        body.querySelectorAll('script').forEach(function(old) {
            var type = (old.getAttribute('type') || '').toLowerCase();
            if (type && type !== 'text/javascript' && type !== 'module') return;
            var s = document.createElement('script');
            Array.prototype.forEach.call(old.attributes, function (attr) {
                s.setAttribute(attr.name, attr.value);
            });
            s.textContent = old.textContent;
            old.parentNode.replaceChild(s, old);
        });

        if (window.htmx) htmx.process(el);
        if (window.rfReinitPlugins) window.rfReinitPlugins(el);
        if (window._cc) {
            if (window._cc.initAutoResize) el.querySelectorAll('[data-autoresize]').forEach(window._cc.initAutoResize);
            if (window._cc.initClamp) el.querySelectorAll('[data-clamp]').forEach(window._cc.initClamp);
            if (window._cc.applyClampList) el.querySelectorAll('[data-clamp-list]').forEach(window._cc.applyClampList);
        }
        // Hide this sheet's "Show more" toggles whose text fits (clamp.js measures only on
        // load and htmx swaps, and a sheet loads by plain XHR).  Scoped to the sheet, unlike
        // `clampInitToggles`: a page-wide pass hides the "Show less" of an expanded clamp
        // and every toggle in a hidden section (both measure as fitting).  A clamp that is not
        // rendered here (a hidden tab or collapsed card) measures 0, so it keeps its toggle.
        el.querySelectorAll('[data-clamp-toggle]').forEach(function (btn) {
            var target = btn.previousElementSibling;
            if (!target || !target.classList.contains('clamp')) return;
            if (!target.getClientRects().length) return;
            btn.style.display = target.scrollHeight > target.clientHeight + 1 ? '' : 'none';
        });
        el.dispatchEvent(new CustomEvent('sheet:ready', { bubbles: true }));
        requestAnimationFrame(function() { body.style.visibility = ''; });
    }

    // ── Load sheet via AJAX ──────────────────────────────────────────────

    // Set the restored scroll position once every request the reloaded body
    // started has swapped in. Lazy cards (hx-trigger="load") above the saved
    // position grow the page, and a card can load another; setting it earlier
    // lands short or gets clamped by the shorter body.
    function finishScrollRestore(r) {
        if (r.pending || scrollRestore !== r) return;
        scrollRestore = null;
        r.body.scrollTop = r.top;
    }

    // htmx runs a request's swap inside the XHR's load handler, so `loadend`
    // fires after the new content (and any requests it started) is in place.
    document.addEventListener('htmx:beforeSend', function (e) {
        var r = scrollRestore;
        if (!r || !r.body.contains(e.target)) return;
        r.pending++;
        e.detail.xhr.addEventListener('loadend', function () {
            r.pending--;
            finishScrollRestore(r);
        });
    });

    // Center a [data-sheet-scroll-to] element that loads below the fold (the
    // Flag for refund form's preselected reason).  Only the body scrolls: the
    // sheet may still be sliding in, so scrollIntoView could move the page too.
    function scrollToMarked(body) {
        var target = body.querySelector('[data-sheet-scroll-to]');
        if (!target) return;
        var rect = target.getBoundingClientRect();
        var box = body.getBoundingClientRect();
        if (target.getAttribute('data-sheet-scroll-to') === 'top') {
            body.scrollTop += rect.top - box.top;
            return;
        }
        if (rect.bottom > box.bottom) body.scrollTop += (rect.top + rect.bottom - box.top - box.bottom) / 2;
    }

    // scrollTop: where to put the body once it loads (Back); omitted, the top;
    // with no Back position, a [data-sheet-scroll-to] element is scrolled into view.
    function loadSheet(url, scrollTop) {
        var el;
        activeUrl = url;
        scrollRestore = null;   // a newer load drops any restore still waiting

        if (activeSheet) {
            el = activeSheet;
            el.querySelector('.sheet__body').innerHTML = SKELETON;
        } else {
            el = document.createElement('div');
            el.className = 'sheet';
            el.setAttribute('data-sheet-dynamic', '');
            el.innerHTML =
                '<div class="sheet__handle"></div>' +
                '<div class="sheet__toolbar">' +
                    '<div></div>' +
                    '<div class="overlay-toolbar__actions">' +
                        '<button class="overlay-toolbar__btn sheet__close">' +
                            '<i class="icon icon-close"></i>' +
                        '</button>' +
                    '</div>' +
                '</div>' +
                '<div class="sheet__header"><h3></h3></div>' +
                '<div class="sheet__actionbar" style="display:none;"></div>' +
                '<div class="sheet__body">' + SKELETON + '</div>' +
                '<div class="sheet__footer" id="active-sheet-footer" style="display:none;"></div>';

            document.body.appendChild(el);

            el.querySelector('.sheet__close').addEventListener('click', function () {
                closeSheet(el);
            });

            openSheet(el);
        }

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        // Force revalidation — prevent browser from serving a cached full-page
        // response when the same URL was visited via normal navigation.
        xhr.setRequestHeader('Cache-Control', 'no-cache');
        xhr.onload = function () {
            var sheetBody = el.querySelector('.sheet__body');
            var redirectUrl = xhr.getResponseHeader('HX-Redirect');
            if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
            }
            if (xhr.status >= 200 && xhr.status < 300) {
                // Armed before populate: htmx.process() starts the load
                // triggers' requests inside it.
                var restore = scrollTop != null ? { body: sheetBody, top: scrollTop, pending: 0 } : null;
                scrollRestore = restore;
                populateSheet(el, xhr.responseText);
                sheetBody.scrollTop = 0;
                if (restore) finishScrollRestore(restore);
                else scrollToMarked(sheetBody);
            } else {
                sheetBody.innerHTML =
                    '<p style="padding: 20px; color: #dc3545;">Failed to load (' + xhr.status + ')</p>';
            }
        };
        xhr.onerror = function () {
            el.querySelector('.sheet__body').innerHTML =
                '<p style="padding: 20px; color: #dc3545;">Network error</p>';
        };
        xhr.send();
    }

    // ── Refresh current sheet ────────────────────────────────────────────

    function refreshSheet() {
        var params = new URLSearchParams(window.location.search);
        var sheetUrl = params.get('sheet');
        if (!sheetUrl || !activeSheet) return;
        loadSheet(sheetUrl);
    }

    // ── Event listeners ──────────────────────────────────────────────────

    document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest('[data-toggle="dropdown"]') || e.target.closest('[data-dropdown]')) return;
        // The dialer dock is a persistent global widget that floats ABOVE the sheet
        // backdrop — interacting with it must never close an open sheet.
        if (e.target.closest('#dialer-dock')) return;

        if (e.target.classList.contains('backdrop') && activeSheet) {
            closeSheet();
            return;
        }

        var dismiss = e.target.closest('[data-dismiss="sheet"]');
        if (dismiss) {
            var sheet = dismiss.closest('.sheet');
            if (sheet) closeSheet(sheet);
            return;
        }

        if (e.target.closest('[data-sheet-back]')) {
            e.preventDefault();
            history.back();
            return;
        }

        var returnBtn = e.target.closest('[data-sheet-return]');
        if (returnBtn) {
            e.preventDefault();
            goBack(returnBtn.getAttribute('data-sheet-return'));
            return;
        }

        var trigger = e.target.closest('[data-sheet]');
        // A control inside a clickable table row (a `tr` with `data-sheet`) does its own thing.
        var control = trigger && trigger.tagName === 'TR' && e.target.closest('a, button, input, select, textarea, label, [data-dropdown]');
        if (control && control !== trigger && trigger.contains(control)) trigger = null;
        if (trigger) {
            e.preventDefault();
            var url = trigger.getAttribute('data-sheet');
            if (activeSheet && trigger.closest('.sheet') === activeSheet) {
                pushTrail();
            } else {
                sheetTrail = [];
            }
            loadSheet(url);
            setSheetParam(url, 'push');
        }
    });

    // An OOB swap that replaces the header or the toolbar slot (a status
    // change re-render) takes the arrow with it; put it back.
    document.body.addEventListener('htmx:oobAfterSwap', function () {
        if (activeSheet && sheetTrail.length && !activeSheet.querySelector('[data-sheet-back]')) {
            renderBackArrow(activeSheet);
        }
    });

    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && document.querySelector('.modal.show')) {
            e._rfModalWasOpen = true;
        }
    }, true);

    document.addEventListener('keydown', function (e) {
        if (e._rfModalWasOpen) return;
        if (e.key === 'Escape' && activeSheet) closeSheet();
    });

    // Back from a sheet to the page under it only closes the sheet, so stop the
    // event there. htmx-nav.js's fallback and htmx's own handler would otherwise
    // reload the page for an entry they did not create, throwing away its state
    // (the rail panel's open section, scroll, unsaved input). Both register at
    // DOMContentLoaded; this registers while the bundle loads, so it runs first.
    window.addEventListener('popstate', function (e) {
        var params = new URLSearchParams(window.location.search);
        var sheetUrl = params.get('sheet');
        if (sheetUrl) {
            var top = sheetTrail[sheetTrail.length - 1];
            var scrollTop = null;
            if (top && top.url === sheetUrl) scrollTop = sheetTrail.pop().scrollTop;
            loadSheet(sheetUrl, scrollTop);
        } else if (activeSheet) {
            var samePage = window.location.pathname === sheetPagePath;
            closeSheet();
            if (samePage) e.stopImmediatePropagation();
        }
    });

    // A boosted (HTMX) content navigation swaps #page-content in place but leaves any open sheet
    // orphaned — sheets are appended to <body>, outside the swap target — so its fixed close-X would
    // linger on the next page and do nothing when clicked. Dismiss every sheet when the main content
    // navigates. A sheet's own internal HTMX (row toggles, form posts) targets elements inside the
    // sheet, never #page-content, so this only fires on a real page change.
    document.body.addEventListener('htmx:beforeSwap', function (e) {
        var target = e.detail && e.detail.target;
        if (!target || target.id !== 'page-content') return;
        document.querySelectorAll('.sheet').forEach(function (el) { closeSheet(el); });
    });

    var initParams = new URLSearchParams(window.location.search);
    var initSheetUrl = initParams.get('sheet');
    if (initSheetUrl) loadSheet(initSheetUrl);

    // Back to the sheet on top of the trail (popstate reloads it at the scroll
    // position it was left at), or, with no trail, the fallback in this
    // sheet's place.
    function goBack(fallbackUrl) {
        if (sheetTrail.length) {
            history.back();
        } else if (fallbackUrl) {
            loadSheet(fallbackUrl);
            setSheetParam(fallbackUrl, 'replace');
        } else {
            closeSheet();
        }
    }

    // ── Public API ───────────────────────────────────────────────────────

    window.rfSheet = {
        open: function (url, options) {
            var historyMode = options && options.history === 'replace' ? 'replace' : 'push';
            // A form sheet that saves and reopens the sheet it was opened from
            // (Edit offer → buyer lead) goes Back instead: popstate pops the trail
            // and reloads that sheet, so it gets no arrow pointing at itself and
            // the browser history keeps no duplicate entry.
            var top = sheetTrail[sheetTrail.length - 1];
            if (activeSheet && historyMode === 'replace' && top && top.url === url) {
                history.back();
                return;
            }
            // A replace (form step → confirm) keeps the trail; the new sheet
            // stands where the old one did.
            if (activeSheet && historyMode === 'push') pushTrail();
            else if (!activeSheet) sheetTrail = [];
            loadSheet(url);
            setSheetParam(url, historyMode);
        },
        back: goBack,
        // The active sheet now answers to `url`, with no reload: Back or a page refresh loads that.
        setUrl: function (url) {
            activeUrl = url;
            setSheetParam(url, 'replace');
        },
        close: closeSheet,
        refresh: refreshSheet,
        active: function () { return activeSheet; },
        // Re-render the active sheet from an HTML payload through the full
        // populate pipeline (toolbar/footer extraction, script eval). Used by
        // htmx-actions.js to display 422 validation re-renders that the
        // form's hx-swap="none" would otherwise discard.
        render: function (html) {
            if (activeSheet) populateSheet(activeSheet, html);
        },

        // The placeholder a loading sheet body shows, for other surfaces that swap in
        // content of their own (the acqs email rail panel).
        skeleton: SKELETON
    };

})();
